import { FileText, ChevronDown, ChevronUp, XCircle, Edit2, Save, AlertTriangle } from 'lucide-react';
import { colors } from '../../styles/colors';
import ActionButton from '../ActionButton';
import ReceiptDataForm from './ReceiptDataForm';

function ScanResultCard({
  result,
  index,
  isExpanded,
  isEditing,
  editedData,
  validationErrors,
  isSaving,
  onToggleExpand,
  onEdit,
  onCancelEdit,
  onSave,
  onFieldChange
}) {
  // Utiliser les erreurs de validation du scan si on n'est pas en mode édition
  const displayValidationErrors = isEditing ? validationErrors : (result.validationErrors || {});
  const styles = {
    pageResultCard: {
      backgroundColor: colors.background.white,
      borderRadius: '8px',
      marginBottom: '1rem',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: colors.border.light,
      overflow: 'hidden',
    },
    pageResultHeader: {
      padding: '1rem 1.5rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      transition: 'background-color 0.2s',
      backgroundColor: colors.background.light,
    },
    pageResultHeaderLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      flex: 1,
    },
    pageResultTitle: {
      margin: 0,
      fontSize: '1.125rem',
      fontWeight: '600',
      color: colors.text.dark,
    },
    pageAmount: {
      fontSize: '1rem',
      fontWeight: '500',
      color: colors.primary.teal,
    },
    pageResultContent: {
      padding: '1.5rem',
      backgroundColor: colors.background.white,
    },
    errorMessage: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '1rem',
      backgroundColor: colors.background.lightRed,
      color: colors.text.darkRed,
      borderRadius: '6px',
    },
    fullTextSection: {
      marginTop: '1.5rem',
    },
    subsectionTitle: {
      fontSize: '1rem',
      fontWeight: '600',
      color: colors.text.dark,
      marginBottom: '1rem',
      marginTop: 0,
    },
    textArea: {
      width: '100%',
      padding: '1rem',
      borderRadius: '6px',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: colors.border.light,
      fontSize: '0.875rem',
      fontFamily: 'monospace',
      backgroundColor: colors.background.light,
      color: colors.text.dark,
      resize: 'vertical',
    },
    confidence: {
      fontSize: '0.875rem',
      fontWeight: '400',
      color: colors.text.secondary,
      marginLeft: '0.5rem',
    },
  };

  return (
    <div style={styles.pageResultCard}>
      {/* Header de la page (cliquable) */}
      <div
        style={styles.pageResultHeader}
        onClick={onToggleExpand}
      >
        <div style={styles.pageResultHeaderLeft}>
          <FileText size={20} style={{ color: colors.primary.teal }} />
          <h3 style={styles.pageResultTitle}>
            Page {result.pageNumber}
            {result.data?.montantTTC && (
              <span style={styles.pageAmount}> - {result.data.montantTTC} € TTC</span>
            )}
            {!result.data?.montantTTC && result.data?.montantHT && (
              <span style={styles.pageAmount}> - {result.data.montantHT} € HT</span>
            )}
          </h3>
          {/* Icône d'alerte si erreurs de validation */}
          {result.hasValidationErrors && !isEditing && (
            <AlertTriangle size={20} style={{ color: colors.status.warning, marginLeft: '0.5rem' }} />
          )}
        </div>
        {isExpanded ? (
          <ChevronUp size={20} style={{ color: colors.text.secondary }} />
        ) : (
          <ChevronDown size={20} style={{ color: colors.text.secondary }} />
        )}
      </div>

      {/* Contenu détaillé (pliable) */}
      {isExpanded && result.success && result.data && (
        <div style={styles.pageResultContent}>
          {/* Boutons Edit/Save/Cancel */}
          <div style={{
            display: 'flex',
            gap: '0.75rem',
            marginBottom: '1.5rem',
            justifyContent: 'flex-end'
          }}>
            {isEditing ? (
              <>
                <ActionButton
                  text="Annuler"
                  onClick={onCancelEdit}
                  variant="secondary"
                  disabled={isSaving}
                />
                <ActionButton
                  icon={Save}
                  text="Enregistrer"
                  onClick={onSave}
                  loading={isSaving}
                />
              </>
            ) : (
              <ActionButton
                icon={Edit2}
                text="Éditer"
                onClick={onEdit}
                variant="secondary"
              />
            )}
          </div>

          {/* Formulaire de données */}
          <ReceiptDataForm
            data={result.data}
            isEditing={isEditing}
            editedData={editedData}
            validationErrors={displayValidationErrors}
            onFieldChange={onFieldChange}
          />

          {/* Texte complet */}
          <div style={styles.fullTextSection}>
            <h4 style={styles.subsectionTitle}>
              Texte complet détecté
              {result.confidence && (
                <span style={styles.confidence}>
                  (Confiance: {(result.confidence * 100).toFixed(0)}%)
                </span>
              )}
            </h4>
            <textarea
              value={result.text}
              readOnly
              style={styles.textArea}
              rows={8}
            />
          </div>
        </div>
      )}

      {/* Message d'erreur si le scan a échoué */}
      {isExpanded && !result.success && (
        <div style={styles.pageResultContent}>
          <div style={styles.errorMessage}>
            <XCircle size={20} />
            <p style={{ margin: 0 }}>
              {result.error || 'Erreur lors du scan de cette page'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default ScanResultCard;
